/**
 * L2 blocks — SectionHeader · KeyValueList · TextBlock · Disclosure · Timeline.
 *
 * The small structural modules every detail surface repeats: a heading with a
 * count, a table of label/value rows, a body of long text, a collapsible
 * section, a milestone strip. None of them owns data
 * behaviour — each binds a list or a value and renders it — which is exactly
 * why they are generic L2 and not per-widget code.
 *
 * They exist because the alternative is worse: without them a blueprint either
 * hand-builds the same Stack-of-Text six times per widget (and drifts), or a
 * domain L3 gets invented for what is really a heading.
 */
import { useEffect, useState } from 'react';
import { TYPE_STYLE, toneColor, toneSoft, gapValue, resetButton, hitTarget, fmt, kvLines, waitsForData } from './ui';
import { Image } from './primitives';
import { SkeletonText } from './feedback';

/** Every block reads its list off `props.<name>` or off the node's own bind. */
function listOf(source, data) {
  const raw = source !== undefined ? source : data;
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
}

/**
 * SectionHeader — a heading, an optional COUNT pill, an optional trailing
 * action. Repeated on essentially every detail surface, which is the whole
 * argument for it being one component instead of six hand-built rows.
 *
 * The count is bound and formatted through the kit's ONE formatter, so a
 * heading and the list under it can never disagree about how many there are.
 *
 * FOUR MORE READINGS, because a heading over a shelf is doing more work than a
 * heading over a list, and every one of them was being dropped:
 *   `sub`   a second line under the title — what the section IS ("Current
 *           cultural moments"), which the title alone never says.
 *   `meta`  right-aligned free text ("11 templates"). It is NOT the `count`
 *           pill: a pill is a chip of chrome, and prod's is a phrase. Both may
 *           be present; they are different readings of the same section.
 *   `hint`  the discoverability affordance for a section the user has to
 *           SCROLL ("Swipe →"). On a rail with no scrollbar, dropping it costs
 *           the user every item past the second one.
 *   `tone`  the title's status colour, for the one heading that is itself a
 *           warning. Absent, the title keeps the plain ink it always had.
 * With none of them declared this renders byte-for-byte what it always did:
 * the four-part head is a DIFFERENT shape (a copy column beside a meta
 * column), and a heading that asked for none of it must not silently move.
 */
export function SectionHeader({ variant, node = null, props = {}, data, ctx, emit }) {
  const title = props.title !== undefined ? props.title : data;
  const count = props.count;
  const action = props.action || null;
  const sub = props.sub == null || props.sub === '' ? null : String(props.sub);
  // `meta` is the one line here that is routinely a COUNT phrased as copy
  // ("11 templates"), so it is the one that reads "0 templates" for a moment
  // while the feed lands. Shimmer it until the read it interpolates is real.
  const metaWaits = waitsForData(ctx, node?.props?.meta);
  const meta = props.meta == null || props.meta === '' ? null : String(props.meta);
  const hint = props.hint == null || props.hint === '' ? null : String(props.hint);
  // OPT-IN inline action (default false = the stacked four-part head, unchanged):
  // with `inlineAction` AND a `meta` present, `meta` and the trailing action sit
  // on ONE horizontal row instead of stacking. A heading that asked for none of
  // it must not silently move, so this is gated on the prop AND on there being a
  // meta+action pair to inline.
  const inlineAction = props.inlineAction === true;
  const titleStyle = {
    ...(variant === 'eyebrow' ? TYPE_STYLE.eyebrow : TYPE_STYLE.subtitle),
    color: props.tone ? toneColor(props.tone) : (variant === 'eyebrow' ? 'var(--text-3)' : 'var(--text)'),
    fontFamily: variant === 'eyebrow' ? undefined : 'var(--font-display)',
    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };
  const countPill = count != null && count !== '' ? (
    <span style={{
      flex: '0 0 auto', padding: '1px 8px', borderRadius: 'var(--r-pill)',
      background: 'var(--surface-3)', color: 'var(--text-3)',
      fontSize: 'var(--t-xs)', fontWeight: 700,
    }}>{fmt(count)}</span>
  ) : null;
  const actionButton = action ? (
    <button
      type="button"
      onClick={() => emit?.('actionActivated', { action: action.action || action.key || action.label })}
      style={{ ...resetButton, color: 'var(--accent)', fontSize: 'var(--t-sm)', fontWeight: 600, flex: '0 0 auto' }}
    >{action.label || 'More'}</button>
  ) : null;

  if (sub || meta || hint || metaWaits) {
    const metaNode = !metaWaits && meta ? (
      <span style={{
        fontSize: 'var(--t-xs)', fontWeight: 700, color: 'var(--text-3)', whiteSpace: 'nowrap',
      }}>{meta}</span>
    ) : null;
    const subNode = sub ? (
      <div style={{
        marginTop: 'var(--sp-1)', fontSize: 'var(--t-sm)',
        color: 'var(--text-3)', lineHeight: 1.3,
      }}>{sub}</div>
    ) : null;
    const hintNode = hint ? (
      <span style={{
        fontSize: 'var(--t-xs)', fontWeight: 700, color: 'var(--text-2)', whiteSpace: 'nowrap',
      }}>{hint}</span>
    ) : null;
    // `meta` + `hint` share ONE right-aligned row when BOTH are present — the
    // default (non-`inlineAction`) head: scene-studio rails put "12 templates"
    // and "Swipe →" on one line, not stacked. Only one of the two present → the
    // stacked slot is unchanged. The Flutter twin composes the same single row
    // (`buildViewIrSectionHeader`).
    const metaHintRow = metaNode && hintNode;

    // INLINE ACTION (opt-in): the meta rides on the LEFT beside the title and the
    // action sits ALONE on the far right, so the row reads
    // `[title  meta] ————— [action]` — `justify-content: space-between` opens the
    // gap. This is the shape a 'N templates … More' head wants. A default header
    // (no `inlineAction`) keeps the stacked four-part head below, byte-identical.
    if (inlineAction && metaNode && actionButton) {
      return (
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 'var(--sp-3)', minWidth: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', minWidth: 0 }}>
              <div style={titleStyle}>{title == null ? '' : String(title)}</div>
              {countPill}
              {metaNode}
            </div>
            {subNode}
          </div>
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            {hintNode}
            {actionButton}
          </div>
        </div>
      );
    }

    return (
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        gap: 'var(--sp-3)', minWidth: 0,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={titleStyle}>{title == null ? '' : String(title)}</div>
          {subNode}
        </div>
        <div style={{
          flex: '0 0 auto', display: 'flex', flexDirection: 'column',
          alignItems: 'flex-end', gap: 2,
        }}>
          {countPill}
          {metaWaits ? <SkeletonText width={64} height={10} /> : null}
          {metaHintRow ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
              {metaNode}
              {hintNode}
            </div>
          ) : (
            <>
              {metaNode}
              {hintNode}
            </>
          )}
          {actionButton}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', minWidth: 0 }}>
      <div style={titleStyle}>{title == null ? '' : String(title)}</div>
      {countPill}
      <span style={{ flex: 1 }} />
      {actionButton}
    </div>
  );
}

/**
 * The VALUE half of one kv row: a scalar reads as one line, an object reads as
 * its own small table (`kvLines`), and an enum reads through `labels`.
 */
function KeyValueCell({ lines, tone, style }) {
  if (lines.length === 1 && !lines[0].label) {
    return <span style={style}>{lines[0].text}</span>;
  }
  return (
    <span style={{ ...style, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {lines.map((l, i) => (
        <span key={`${l.label || 'l'}-${i}`} style={{ display: 'flex', gap: 6, minWidth: 0 }}>
          {l.label ? (
            <span style={{ color: 'var(--text-3)', flex: '0 0 auto', fontWeight: 500 }}>{l.label}</span>
          ) : null}
          <span style={{ minWidth: 0, color: toneColor(tone) }}>{l.text}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * KeyValueList — the detail rows every data view ends in.
 *
 * SEALED: an EMPTY value is DROPPED, not drawn blank (`hideEmpty`, default
 * on). A row that reads "Brand —" says the field exists and is unknown; on a
 * detail page that is noise, and the honest render of "we do not know" is
 * silence. Two shapes from one component: stacked rows (`columns: 1`) and a
 * tile grid (`columns: 2`), because a facts grid and a details table differ by
 * column count, not by contract.
 *
 * TWO READINGS of the value it prints, both from `kvLines`:
 *   `labels: {field: {value: "Label"}}` decodes an ENUM — the row stores
 *   `supplement` and the page says "Supplement", without a derive per enum.
 *   An OBJECT field draws as its own small table instead of `JSON.stringify`,
 *   which is what put `{"verdict":"caution",…}` on a nutrition page.
 */
export function KeyValueList({ variant, props = {}, data }) {
  const items = listOf(props.items, data)
    .map((it) => {
      const field = it.key ?? it.field ?? it.label ?? '';
      return {
        label: it.label ?? it.key ?? '',
        lines: kvLines(it.value, { labels: props.labels, field, exclude: props.exclude }),
        tone: it.tone,
      };
    })
    .filter((it) => (props.hideEmpty === false) || it.lines.length);
  if (!items.length) return null;

  const columns = Number(props.columns) || (variant === 'grid' ? 2 : 1);

  if (columns > 1) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))`, gap: 'var(--sp-2)' }}>
        {items.map((it, i) => (
          <div key={`${it.label}-${i}`} style={{
            background: 'var(--surface)', borderRadius: 'var(--r)', padding: 'var(--sp-3)',
            boxShadow: 'var(--shadow-1)', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0,
          }}>
            <span style={{ ...TYPE_STYLE.eyebrow, color: 'var(--text-3)' }}>{it.label}</span>
            <KeyValueCell
              lines={it.lines}
              tone={it.tone}
              style={{
                fontSize: 'var(--t-md)', fontWeight: 600, color: toneColor(it.tone), minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <dl style={{
      margin: 0, background: 'var(--surface)', borderRadius: 'var(--r)',
      padding: '2px var(--sp-4)', boxShadow: 'var(--shadow-1)',
    }}>
      {items.map((it, i) => (
        <div key={`${it.label}-${i}`} style={{
          display: 'flex', gap: 'var(--sp-3)', padding: '10px 0',
          borderTop: i === 0 ? 'none' : '1px solid var(--line-soft)',
        }}>
          <dt style={{ flex: '0 0 40%', fontSize: 'var(--t-sm)', color: 'var(--text-3)' }}>{it.label}</dt>
          <dd style={{ margin: 0, flex: 1, minWidth: 0, fontSize: 'var(--t-sm)', wordBreak: 'break-word' }}>
            <KeyValueCell lines={it.lines} tone={it.tone} style={{ color: toneColor(it.tone) }} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** One block of a `prose` body — the closed set a declared document may use. */
function ProseBlock({ block }) {
  const type = block?.block || block?.type || 'paragraph';
  switch (type) {
    case 'heading':
      return <div style={{ ...TYPE_STYLE.subtitle, fontFamily: 'var(--font-display)' }}>{block.text}</div>;
    case 'bullets':
      return (
        <ul style={{ margin: 0, paddingLeft: '1.1em', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(block.items || []).map((t, i) => (
            <li key={i} style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)', lineHeight: 1.5 }}>
              {typeof t === 'string' ? t : (t?.text ?? '')}
            </li>
          ))}
        </ul>
      );
    case 'figure':
      return (
        <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Image props={{ src: block.src, fit: 'cover', ratio: block.ratio || '4/3' }} />
          {block.caption ? (
            <figcaption style={{ fontSize: 'var(--t-xs)', color: 'var(--text-3)' }}>{block.caption}</figcaption>
          ) : null}
        </figure>
      );
    case 'chips':
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {(block.items || []).map((c, i) => {
            const label = typeof c === 'string' ? c : (c?.text ?? c?.label ?? '');
            const tone = typeof c === 'object' ? c?.tone : undefined;
            return (
              <span key={`${label}-${i}`} style={{
                padding: '2px 8px', borderRadius: 'var(--r-pill)',
                background: toneSoft(tone), color: toneColor(tone),
                fontSize: 'var(--t-xs)', fontWeight: 700,
              }}>{label}</span>
            );
          })}
        </div>
      );
    default:
      return (
        <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--text-2)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
          {typeof block === 'string' ? block : (block?.text ?? '')}
        </p>
      );
  }
}

/**
 * TextBlock — LONG text, which `Text` (a typographic run) is deliberately not.
 *
 * Three readings from one contract: `plain` (a multi-paragraph body, newlines
 * preserved), `mono` (extracted / machine text, wrapped and its own scroll so
 * a 4 000-character OCR dump cannot push the rest of the page off screen), and
 * `prose` (a DECLARED block sequence — heading · paragraph · bullets · figure ·
 * chips — which is how a model-written document renders without a per-widget
 * card).
 *
 * SEALED: `hideWhenEmpty` (default on) — an empty body draws NOTHING, never a
 * section heading over an empty well.
 */
export function TextBlock({ variant, props = {}, data }) {
  const value = props.value !== undefined ? props.value : data;
  const kind = props.variant || variant || 'plain';
  const empty = value == null || value === ''
    || (Array.isArray(value) && !value.length);
  if (empty && props.hideWhenEmpty !== false) return null;

  const box = {
    maxHeight: props.maxHeight || undefined,
    overflowY: props.scroll ? 'auto' : undefined,
    scrollbarWidth: props.scroll ? 'none' : undefined,
  };

  if (kind === 'prose') {
    const blocks = Array.isArray(value) ? value : [value];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', minWidth: 0, ...box }}>
        {blocks.map((b, i) => <ProseBlock key={i} block={b} />)}
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: kind === 'mono' ? 'var(--font-mono)' : 'inherit',
      fontSize: kind === 'mono' ? 'var(--t-xs)' : 'var(--t-sm)',
      color: 'var(--text-2)', lineHeight: 1.55,
      whiteSpace: props.wrap === 'normal' ? 'normal' : 'pre-wrap',
      wordBreak: 'break-word', minWidth: 0,
      background: kind === 'mono' ? 'var(--surface-2)' : undefined,
      borderRadius: kind === 'mono' ? 'var(--r-sm)' : undefined,
      padding: kind === 'mono' ? 'var(--sp-3)' : undefined,
      ...box,
    }}>
      {Array.isArray(value) ? value.filter(Boolean).join('\n') : String(value ?? '')}
    </div>
  );
}

/**
 * Disclosure — a summary row that reveals its children.
 *
 * Its whole job is to keep a secondary affordance ON the page without spending
 * the page's attention on it, which is why its fallback is `Stack`: an app
 * that cannot collapse simply shows the content, and nothing is lost but the
 * folding.
 */
export function Disclosure({ props = {}, emit, slots, children }) {
  const [shown, setOpen] = useState(Boolean(props.open));
  const setOpenSlot = slots?.setOpen;

  // The slot SEEDS the fold; the user can still fold it back, which a
  // shadowing read of the inbox would have made impossible.
  useEffect(() => {
    if (setOpenSlot?.payload?.open !== undefined) setOpen(Boolean(setOpenSlot.payload.open));
  }, [setOpenSlot]);

  const toggle = () => {
    const next = !shown;
    setOpen(next);
    emit?.('toggled', { open: next });
  };

  return (
    <div style={{
      background: 'var(--surface)', borderRadius: 'var(--r)', boxShadow: 'var(--shadow-1)',
      padding: 'var(--sp-2) var(--sp-3)', minWidth: 0,
    }}>
      <button
        type="button"
        aria-expanded={shown}
        onClick={toggle}
        style={{
          ...resetButton, ...hitTarget,
          width: '100%', display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
          fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--text)',
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>{props.summary}</span>
        <span aria-hidden style={{
          color: 'var(--text-3)', transition: 'transform 160ms var(--ease)',
          transform: shown ? 'rotate(90deg)' : 'none',
        }}>›</span>
      </button>
      {shown ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: gapValue(props.gap, 'var(--sp-3)'), padding: 'var(--sp-2) 0' }}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Timeline — a CONNECTED milestone strip: node · primary · secondary, in
 * order, with the connector drawn between them.
 *
 * Horizontal by default (a due-date strip), and its declared fallback is a
 * Stack, which is the same rows read vertically — the connector is the form,
 * the sequence is the function.
 */
export function Timeline({ variant, props = {}, data, emit }) {
  const nodes = listOf(props.nodes, data);
  if (!nodes.length) return null;
  const column = variant === 'column' || props.direction === 'column';

  return (
    <div style={{
      display: 'flex', flexDirection: column ? 'column' : 'row',
      gap: 0, overflowX: column ? undefined : 'auto', scrollbarWidth: 'none', minWidth: 0,
    }}>
      {nodes.map((n, i) => {
        const tone = n.tone || 'accent';
        return (
          <div
            key={`${n.node ?? n.primary ?? i}-${i}`}
            onClick={emit ? () => emit('nodeActivated', { index: i, node: n.node ?? null }) : undefined}
            style={{
              display: 'flex', flexDirection: column ? 'row' : 'column',
              alignItems: column ? 'flex-start' : 'center',
              gap: 'var(--sp-2)', flex: column ? undefined : '1 0 92px', minWidth: 0,
              position: 'relative', padding: column ? '0 0 var(--sp-3)' : '0 var(--sp-2)',
              cursor: emit ? 'pointer' : 'default',
            }}
          >
            {/* the connector: a hairline to the NEXT node, never past the last */}
            {i < nodes.length - 1 ? (
              <span aria-hidden style={column
                ? { position: 'absolute', left: 9, top: 20, bottom: 0, width: 1, background: 'var(--line)' }
                : { position: 'absolute', top: 9, left: '50%', right: '-50%', height: 1, background: 'var(--line)' }} />
            ) : null}
            <span style={{
              width: 20, height: 20, borderRadius: 10, flex: '0 0 auto', zIndex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: toneSoft(tone), color: toneColor(tone),
              border: `1px solid ${toneColor(tone)}`,
              fontSize: 'var(--t-xs)', fontWeight: 800,
            }}>{n.node ?? ''}</span>
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0,
              textAlign: column ? 'left' : 'center',
            }}>
              <span style={{
                fontSize: 'var(--t-xs)', fontWeight: 700, color: 'var(--text)',
                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{n.primary ?? ''}</span>
              {n.secondary ? (
                <span style={{
                  fontSize: 'var(--t-xs)', color: 'var(--text-3)',
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{n.secondary}</span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
